// Function to format a paper object into a basic BibTeX entry
export const formatBibtex = async (paper) => {
    // Try to get BibTeX directly from arXiv if it's an arXiv paper
    const arxivId = extractArxivId(paper.url);
    if (arxivId) {
        try {
            const arxivBibtex = await fetchArxivBibtex(arxivId);
            if (arxivBibtex) {
                return arxivBibtex;
            }
        } catch (error) {
            console.warn(`Failed to fetch BibTeX from arXiv for ${arxivId}:`, error);
            // Continue with fallback method
        }
    }

    // Fallback to generating BibTeX manually
    const bibtexKey = generateBibtexKey(paper);
    const authors = paper.authors?.map(a => a.name).join(' and ') || '';
    const title = paper.title || '';
    const year = paper.year || '';
    const journal = paper.venue || '';
    const url = paper.url || '';
    const abstract = paper.abstract || '';
    let doi = '';

    if (url.startsWith('https://doi.org/')) {
        doi = url.substring(16);
    } else if (url.startsWith('http://doi.org/')) {
        doi = url.substring(15);
    } else if (paper.doi) {
        doi = paper.doi;
    }

    let entry = `@article{${bibtexKey},\n`;
    if (authors) entry += `  author = {${authors}},\n`;
    if (title) entry += `  title = {${title}},\n`;
    if (journal) entry += `  journal = {${journal}},\n`;
    if (year) entry += `  year = {${year}},\n`;
    if (url) entry += `  url = {${url}},\n`;
    if (doi) entry += `  doi = {${doi}},\n`;
    if (abstract) entry += `  abstract = {${abstract}},\n`;
    if (paper.fieldsOfStudy && paper.fieldsOfStudy.length)
        entry += `  keywords = {${paper.fieldsOfStudy.join(', ')}},\n`;
    entry = entry.replace(/,\n$/, '\n');
    entry += '}';

    return entry;
};

// Extract arXiv ID from URL
function extractArxivId(url) {
    if (!url) return null;

    // Match different arXiv URL patterns
    const patterns = [
        /arxiv\.org\/abs\/([0-9v\.]+)/i,      // https://arxiv.org/abs/2101.12345
        /arxiv\.org\/pdf\/([0-9v\.]+)/i,       // https://arxiv.org/pdf/2101.12345.pdf
        /arxiv\.org\/vc\/arxiv\/papers\/\d{4}\/(\d{4}\.\d+v\d+)/i  // Old format, less common
    ];

    for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match && match[1]) {
            // Clean up the ID by removing any file extension
            console.log('arxivId', match[1].replace('.pdf', ''));
            return match[1].replace('.pdf', '');
        }
    }

    return null;
}

// Fetch BibTeX directly from arXiv
async function fetchArxivBibtex(arxivId) {
    try {
        console.log(`Fetching arXiv metadata for ID: ${arxivId}`);

        // Use the environment variable for API URL
        const API_URL = process.env.REACT_APP_API_URL || '';

        // First remove any version suffix (e.g., v1, v2)
        const baseArxivId = arxivId.replace(/v\d+$/, '');

        // Make the API request with fetch
        const response = await fetch(`${API_URL}/api/arxiv-metadata/${baseArxivId}`);

        if (!response.ok) {
            throw new Error(`Failed to fetch arXiv metadata: ${response.status}`);
        }

        const xmlText = await response.text();

        // Parse XML
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlText, "text/xml");

        // Extract data from XML
        const entry = xmlDoc.querySelector('entry');
        if (!entry) throw new Error('No entry found in arXiv response');

        const title = entry.querySelector('title')?.textContent?.trim() || '';
        const summary = entry.querySelector('summary')?.textContent?.trim() || '';
        const published = entry.querySelector('published')?.textContent || '';
        const year = published ? new Date(published).getFullYear() : '';

        // Extract authors
        const authors = Array.from(entry.querySelectorAll('author name'))
            .map(author => author.textContent.trim())
            .join(' and ');

        // Generate BibTeX manually
        const bibtexKey = `arxiv.${baseArxivId.replace('.', '_')}`;

        let bibtex = `@article{${bibtexKey},\n`;
        bibtex += `  author = {${authors}},\n`;
        bibtex += `  title = {${title}},\n`;
        bibtex += `  journal = {arXiv preprint arXiv:${baseArxivId}},\n`;
        if (year) bibtex += `  year = {${year}},\n`;
        bibtex += `  url = {https://arxiv.org/abs/${baseArxivId}},\n`;
        bibtex += `  eprint = {${baseArxivId}},\n`;
        bibtex += `  archivePrefix = {arXiv},\n`;
        if (summary) bibtex += `  abstract = {${summary}},\n`;
        bibtex = bibtex.replace(/,\n$/, '\n');
        bibtex += '}';

        return bibtex;
    } catch (error) {
        console.error('Error generating arXiv BibTeX:', error);
        return null;
    }
}

// Support for both async and sync methods to download BibTeX
export const downloadBibtex = async (papers, filename) => {
    try {
        // Process all papers, waiting for any async BibTeX generation
        const bibtexEntries = await Promise.all(
            papers.map(paper => formatBibtex(paper))
        );

        // Join entries with double newlines
        const allBibtex = bibtexEntries.join('\n\n');

        // Download the file
        downloadToFile(allBibtex, filename, 'application/x-bibtex');
    } catch (error) {
        console.error('Error generating BibTeX:', error);
        alert('Failed to generate BibTeX. See console for details.');
    }
};

// Function to trigger download of text content as a file
export const downloadToFile = (content, filename, contentType = 'text/plain') => {
    const blob = new Blob([content], { type: contentType });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
};

function generateBibtexKey(paper) {
    // Try to use: first author last name + year + first significant word of title
    let authorPart = 'unknown';
    if (paper.authors && paper.authors.length > 0) {
        // Use last name of first author
        const names = paper.authors[0].name.split(' ');
        authorPart = names[names.length - 1].replace(/[^a-zA-Z0-9]/g, '');
    }

    let yearPart = paper.year || '';
    if (!yearPart || isNaN(Number(yearPart))) {
        yearPart = 'ND';
    }

    // Use first significant word from title (skip stopwords)
    let titlePart = 'untitled';
    if (paper.title) {
        const stopwords = ['the', 'a', 'an', 'of', 'and', 'in', 'on', 'for', 'with', 'to', 'by', 'at', 'from'];
        const words = paper.title.split(/\s+/).map(w => w.replace(/[^a-zA-Z0-9]/g, ''));
        titlePart = words.find(w => w && !stopwords.includes(w.toLowerCase())) || words[0] || 'untitled';
    }

    // If still not unique, use part of DOI or paperId
    let uniquePart = '';
    if (paper.doi) {
        uniquePart = paper.doi.split('/').pop().replace(/[^a-zA-Z0-9]/g, '');
    } else if (paper.paperId) {
        uniquePart = paper.paperId.replace(/[^a-zA-Z0-9]/g, '');
    }

    let key = `${authorPart}${yearPart}_${titlePart}`;
    if (uniquePart && !key.includes(uniquePart)) {
        key += `_${uniquePart}`;
    }
    return key;
}
